import { Theme } from '@mui/material/styles';
import { TFunction } from 'i18next';
import { ComponentConfig } from '@/components/componentRegistry';
import { DisclosureType } from '@/config/enums';

/**
 * Common text styles based on Figma design
 * Responsive font sizes for mobile optimization
 */
const getTextStyles = (theme: Theme) => ({
  body: {
    fontFamily: 'Archivo',
    fontWeight: 400,
    fontSize: { xs: '12px', sm: '14px' },
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: '#4E5C79',
  },
  title: {
    fontFamily: 'Archivo',
    fontWeight: 700,
    fontSize: { xs: '20px', sm: '24px', md: '28px' },
    lineHeight: '36px',
    color: '#2C2D38',
    textAlign: 'center' as const,
  },
  sectionTitle: {
    fontFamily: 'Archivo',
    fontWeight: 400,
    fontSize: { xs: '12px', sm: '14px' },
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: '#4E5C79',
    marginTop: theme.unit(4), // 16px
    marginBottom: theme.unit(2), // 8px
  },
});

/**
 * Build Terms of Use content (simple paragraphs)
 */
const buildTermsOfUseContent = (
  theme: Theme,
  t: TFunction
): ComponentConfig[] => {
  const styles = getTextStyles(theme);
  const paragraphs = [
    t('termsOfUse.paragraph1'),
    t('termsOfUse.paragraph2'),
    t('termsOfUse.paragraph3'),
    t('termsOfUse.paragraph4'),
  ];

  return paragraphs.map((text, index) => ({
    component: 'Typography',
    props: {
      sx: {
        ...styles.body,
        marginBottom: index < paragraphs.length - 1 ? theme.unit(4) : 0, // 16px
      },
    },
    children: [text],
  }));
};

/**
 * Build Confirmation Release content (intro + numbered list)
 */
const buildConfirmationReleaseContent = (
  theme: Theme,
  t: TFunction
): ComponentConfig[] => {
  const styles = getTextStyles(theme);
  const items = t('confirmationRelease.items', {
    returnObjects: true,
  }) as string[];

  return [
    // Intro paragraph
    {
      component: 'Typography',
      props: {
        sx: {
          ...styles.body,
          marginBottom: theme.unit(4), // 16px
        },
      },
      children: [t('confirmationRelease.intro')],
    },
    // Numbered list
    {
      component: 'Box',
      props: {
        component: 'ol',
        sx: {
          ...styles.body,
          margin: 0,
          paddingLeft: theme.unit(6), // 24px
          listStyleType: 'decimal',
          '& li': {
            marginBottom: theme.unit(2), // 8px
            paddingLeft: theme.unit(1), // 4px
          },
          '& li:last-child': {
            marginBottom: 0,
          },
        },
      },
      children: items.map((item) => ({
        component: 'Box',
        props: {
          component: 'li',
        },
        children: [item],
      })),
    },
  ];
};

/**
 * Build a section with title and bullet list
 */
const buildBulletSection = (
  theme: Theme,
  title: string,
  items: string[]
): ComponentConfig[] => {
  const styles = getTextStyles(theme);

  return [
    // Section title
    {
      component: 'Typography',
      props: {
        sx: styles.sectionTitle,
      },
      children: [title],
    },
    // Bullet list
    {
      component: 'Box',
      props: {
        component: 'ul',
        sx: {
          ...styles.body,
          margin: 0,
          paddingLeft: theme.unit(6), // 24px
          listStyleType: 'disc',
          '& li': {
            marginBottom: theme.unit(2), // 8px
            paddingLeft: theme.unit(1), // 4px
            whiteSpace: 'pre-wrap',
          },
          '& li:last-child': {
            marginBottom: 0,
          },
        },
      },
      children: items.map((item) => ({
        component: 'Box',
        props: {
          component: 'li',
        },
        children: [item],
      })),
    },
  ];
};

/**
 * Build Electronic Documents (E-consent) content
 * Has intro paragraphs, multiple sections with bullet lists, and agreement
 */
const buildElectronicDocumentsContent = (
  theme: Theme,
  t: TFunction
): ComponentConfig[] => {
  const styles = getTextStyles(theme);
  const children: ComponentConfig[] = [];

  // Intro paragraphs
  const intros = ['intro1', 'intro2', 'intro3', 'intro4'];
  intros.forEach((key) => {
    children.push({
      component: 'Typography',
      props: {
        sx: {
          ...styles.body,
          marginBottom: theme.unit(4), // 16px
        },
      },
      children: [t(`electronicDocuments.${key}`)],
    });
  });

  // Sections with bullet lists
  const sections = [
    {
      key: 'yourConsent',
      titleKey: 'electronicDocuments.yourConsent.title',
      itemsKey: 'electronicDocuments.yourConsent.items',
    },
    {
      key: 'withdrawalOfConsent',
      titleKey: 'electronicDocuments.withdrawalOfConsent.title',
      itemsKey: 'electronicDocuments.withdrawalOfConsent.items',
    },
    {
      key: 'obtainingPaperCopies',
      titleKey: 'electronicDocuments.obtainingPaperCopies.title',
      itemsKey: 'electronicDocuments.obtainingPaperCopies.items',
    },
    {
      key: 'systemRequirements',
      titleKey: 'electronicDocuments.systemRequirements.title',
      itemsKey: 'electronicDocuments.systemRequirements.items',
    },
    {
      key: 'howWeCanReachYou',
      titleKey: 'electronicDocuments.howWeCanReachYou.title',
      itemsKey: 'electronicDocuments.howWeCanReachYou.items',
    },
  ];

  sections.forEach(({ titleKey, itemsKey }) => {
    const title = t(titleKey);
    const items = t(itemsKey, { returnObjects: true }) as string[];
    children.push(...buildBulletSection(theme, title, items));
  });

  // Agreement paragraph
  children.push({
    component: 'Typography',
    props: {
      sx: {
        ...styles.body,
        marginTop: theme.unit(4), // 16px
      },
    },
    children: [t('electronicDocuments.agreement')],
  });

  return children;
};

/**
 * Get Disclosure page configuration
 * A common config for all disclosure/legal document pages (Terms of Use, Confirmation & Release, Electronic Documents)
 * Based on Figma designs:
 * - Terms of Use: node-id=120-73838
 * - E-consent: node-id=120-73854
 * - Confirmation & Release: node-id=120-73872
 *
 * @param theme - MUI theme object
 * @param t - Translation function
 * @param disclosureType - Type of disclosure to display
 * @returns Component configuration for Disclosure page
 */
export const getDisclosurePageConfig = (
  theme: Theme,
  t: TFunction,
  disclosureType: DisclosureType
): ComponentConfig => {
  const styles = getTextStyles(theme);

  // Get title based on disclosure type
  const titleKey = `${disclosureType}.title`;

  // Build content based on disclosure type
  let contentChildren: ComponentConfig[];
  switch (disclosureType) {
    case DisclosureType.TERMS_OF_USE:
      contentChildren = buildTermsOfUseContent(theme, t);
      break;
    case DisclosureType.CONFIRMATION_RELEASE:
      contentChildren = buildConfirmationReleaseContent(theme, t);
      break;
    case DisclosureType.ELECTRONIC_DOCUMENTS:
      contentChildren = buildElectronicDocumentsContent(theme, t);
      break;
    default:
      contentChildren = [];
  }

  return {
    component: 'Box',
    props: {
      sx: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        width: '100%',
        flex: 1,
        padding: { xs: theme.unit(4), sm: theme.unit(6), md: theme.unit(7.5) },
      },
    },
    children: [
      {
        component: 'Box',
        props: {
          sx: {
            backgroundColor: '#FFFFFF',
            border: '1px solid #D5D5D5',
            borderRadius: theme.unit(2.5), // 10px
            boxShadow:
              '0px 1px 2px 0px rgba(0, 0, 0, 0.08), 0px 2px 6px 2px rgba(0, 0, 0, 0.08)',
            padding: {
              xs: `${theme.unit(6)} ${theme.unit(4)}`,
              sm: `${theme.unit(8)} ${theme.unit(6)}`,
              md: `${theme.unit(12)} 0`,
            },
            width: '100%',
            maxWidth: { xs: '100%', sm: '90%', md: theme.unit(175) },
            display: 'flex',
            justifyContent: 'center',
          },
        },
        children: [
          {
            component: 'Box',
            props: {
              sx: {
                display: 'flex',
                flexDirection: 'column',
                gap: theme.unit(4), // 16px
                width: { xs: '100%', md: theme.unit(150) },
                maxHeight: { xs: 'none', md: theme.unit(160) },
                overflowY: { xs: 'visible', md: 'auto' },
                overflowX: 'hidden',
                // Custom scrollbar styling
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#79747E',
                  borderRadius: '100px',
                },
              },
            },
            children: [
              // Title
              {
                component: 'Box',
                props: {
                  sx: { width: '100%' },
                },
                children: [
                  {
                    component: 'Typography',
                    props: {
                      sx: styles.title,
                    },
                    children: [t(titleKey)],
                  },
                ],
              },
              // Content
              {
                component: 'Box',
                props: {
                  sx: {
                    padding: theme.unit(4), // 16px
                    borderRadius: theme.unit(2), // 8px
                    width: '100%',
                  },
                },
                children: contentChildren,
              },
            ],
          },
        ],
      },
    ],
  };
};
